(ns common.opensearch.days-since
  "The query of a field that shows the days since a time, for example the age in
   days of a deal. The days depend on the day of today, so the index cannot hold
   them. The field names the field that holds the start time
   (`:field/days-since-attribute`), and a sort or a filter of the days uses the
   start time:
   - More days is an earlier start, so a sort of the days is the reverse sort of
     the start.
   - A day count is rounded up, as the view shows it, so `days > n` is a start
     more than `n` days before now."
  (:require
   [clojure.string :as string]
   [common.opensearch.utils :as os-utils]
   [common.schema.registry :as registry]))

(def ^:private millis-per-day
  (* 24 60 60 1000))

(defn now-millis
  "The current time in epoch millis."
  []
  #?(:clj  (System/currentTimeMillis)
     :cljs (.now js/Date)))

(def since-suffix-by-days-suffix*
  "The alias suffix of each days field mapped to the alias suffix of its start
   time, for example `oppo-age-in-days-active` to `oppo-active-since`. A suffix is
   the alias of a single-element path, without the `flat.` prefix and without a
   ref path. The flat alias of a linked record carries a ref path before the
   suffix, so the lookup matches the suffix at any ref path, see `since-alias`."
  (delay
    (->> registry/static-cross-entity-schema
         (keep (fn [[attribute field-schema]]
                 (when-let [since-attribute (:field/days-since-attribute field-schema)]
                   [(os-utils/field-path->alias [attribute])
                    (os-utils/field-path->alias [since-attribute])])))
         (into {}))))

(defn since-alias
  "The flat alias of the start time of the days field with the flat alias
   `days-alias`, or nil when the field does not show days since a time. The days
   field of a linked record has a ref path before its suffix, for example
   `flat.<ref>-oppo-age-in-days-active`. The start time is indexed at the same ref
   path, so the ref path stays and only the suffix changes to the start time."
  [days-alias]
  (when (string/starts-with? days-alias "flat.")
    (let [field-name (subs days-alias (count "flat."))]
      (some (fn [[days-suffix since-suffix]]
              (when-let [ref-path (cond (= field-name days-suffix)
                                        ""
                                        (string/ends-with? field-name (str "-" days-suffix))
                                        (subs field-name 0 (- (count field-name)
                                                              (count days-suffix))))]
                (str "flat." ref-path since-suffix)))
            @since-suffix-by-days-suffix*))))

(defn since-aliases
  "The flat aliases of all the start times at the root, sorted. The index maps
   them as dates, so that a filter of the days can use the date math of
   OpenSearch. The index template and the drift check also accept a ref path
   before a suffix, see `backend.search.dedicated.template` and
   `backend.search.dedicated.index-drift`."
  []
  (->> (vals @since-suffix-by-days-suffix*)
       (distinct)
       (sort)
       (mapv (fn [since-suffix]
               (str "flat." since-suffix)))))

(defn- start-before
  "The time `days` days before `now`, in epoch millis. Without `now`, the date
   math of OpenSearch, for example `now-30d`: the search reads the clock, so the
   code that makes the query stays pure and the query does not get old."
  [now days]
  (if (some? now)
    (long (- now (* days millis-per-day)))
    (str "now" (if (neg? days) "+" "-") (Math/abs (long days)) "d")))

(def ^:private no-match
  "A clause that matches no document."
  {:bool {:must_not [{:match_all {}}]}})

(defn- round-down
  [x]
  (long (Math/floor (double x))))

(defn- round-up
  [x]
  (long (Math/ceil (double x))))

(defn- whole-day-bounds
  "The bounds of whole days that match a comparison with `value`, which can have a
   fraction. The view shows whole days, so `> 30.5` is `> 30` and `>= 30.5` is
   `>= 31`. Nil for `:eq` with a fraction, which no whole day matches."
  [{:keys [operator value value-to]}]
  (case operator
    :gt      {:value (round-down value)}
    :gte     {:value (round-up value)}
    :lt      {:value (round-up value)}
    :lte     {:value (round-down value)}
    :eq      (when (== value (round-down value))
               {:value (round-down value)})
    :between (when (some? value-to)
               {:value    (round-up value)
                :value-to (round-down value-to)})
    nil))

(defn range-clause
  "The clause on `since-field` for days that compare to `value` with `operator`:
   `:gt`, `:gte`, `:lt`, `:lte`, `:eq`, or `:between` with `value-to`. The days are
   whole days rounded up, so a day count `n` is a start in the day
   `(n - 1, n]` days before now. A bound with a fraction compares with the whole
   days, see `whole-day-bounds`. Nil when a value is missing. Without `now`, the
   bounds are date math, see `start-before`."
  [since-field {:keys [operator value now] :as comparison}]
  (let [older-than (fn [days] {:lt (start-before now days)})
        at-most (fn [days] {:gte (start-before now days)})]
    (when (some? value)
      (if-let [{:keys [value value-to]} (whole-day-bounds comparison)]
        (case operator
          :gt      {:range {since-field (older-than value)}}
          :gte     {:range {since-field (older-than (dec value))}}
          :lt      {:range {since-field (at-most (dec value))}}
          :lte     {:range {since-field (at-most value)}}
          :eq      {:range {since-field (merge (at-most value) (older-than (dec value)))}}
          :between (if (<= value value-to)
                     {:range {since-field (merge (at-most value-to) (older-than (dec value)))}}
                     no-match)
          nil)
        ;; No whole day equals a value with a fraction.
        (when (= :eq operator)
          no-match)))))

(defn days
  "The whole days from the start time `since` (epoch millis or an inst) to
   `now-millis`, rounded up as the old grid does. Nil when `since` is nil."
  [since now-millis]
  (when (some? since)
    (long (Math/ceil (/ (double (- now-millis (if (inst? since) (inst-ms since) since)))
                        millis-per-day)))))

(defn sort-order
  "The sort order of the start time for the sort `order` of the days."
  [order]
  (if (= "desc" (name order)) "asc" "desc"))
